
import { X, Settings2 } from 'lucide-react';
import { SlideDrawer } from '@/components/ui/slide-drawer';
import { Button } from '@/components/ui/button';
import type { StateNodeData, AslState } from '@/types/workflow-graph';
import type { CapabilityMetadata, TabId } from './types';
import { useTaskConfig } from './hooks/useTaskConfig';
import { TaskConfigSummaryHeader } from './TaskConfigSummaryHeader';
import { TaskConfigTabs } from './TaskConfigTabs';
import { CapabilityTab } from './tabs/CapabilityTab';
import { InputTab } from './tabs/InputTab';
import { OutputTab } from './tabs/OutputTab';
import { ResilienceTab } from './tabs/ResilienceTab';
import { ErrorsTab } from './tabs/ErrorsTab';
import { AdvancedTab } from './tabs/AdvancedTab';

interface TaskConfigDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    nodeData: StateNodeData | null;
    readOnly?: boolean;
    onSave?: (nodeId: string, updates: Partial<StateNodeData>) => void;
    /** Available capabilities from registry */
    capabilities?: CapabilityMetadata[];
    /** Available nodes for catch rule targets */
    availableNodes?: string[];
}

const tabs: Array<{ id: TabId; label: string }> = [
    { id: 'capability', label: 'Capability' },
    { id: 'input', label: 'Input' },
    { id: 'output', label: 'Output' },
    { id: 'resilience', label: 'Resilience' },
    { id: 'errors', label: 'Errors' },
    { id: 'advanced', label: 'Advanced' },
];

export function TaskConfigDrawer({
    isOpen,
    onClose,
    nodeData,
    readOnly = false,
    onSave,
    capabilities = [],
    availableNodes = [],
}: TaskConfigDrawerProps) {
    const taskConfig = useTaskConfig({
        nodeData,
        capabilities,
        availableNodes,
        onSave: (config, aslState) => {
            if (nodeData) {
                onSave?.(nodeData.label, {
                    state: { ...nodeData.state, ...aslState } as AslState,
                });
            }
            onClose();
        },
    });

    if (!nodeData) return null;

    return (
        <SlideDrawer isOpen={isOpen} onClose={onClose}>
                        {/* Header */}
                        <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100">
                                        <Settings2 className="h-5 w-5 text-blue-600" />
                                    </div>
                                    <div>
                                        <h2 className="text-xl font-semibold text-slate-900">
                                            Task Configuration
                                        </h2>
                                        <p className="text-sm text-slate-500">
                                            {nodeData.label}
                                        </p>
                                    </div>
                                </div>
                                <button
                                    onClick={onClose}
                                    className="rounded-full p-2 hover:bg-slate-200 text-slate-400 hover:text-slate-600 transition-colors"
                                >
                                    <X size={20} />
                                </button>
                            </div>
                        </div>

                        {/* Summary Header */}
                        <TaskConfigSummaryHeader
                            config={taskConfig.config}
                            capability={taskConfig.selectedCapability}
                        />

                        {/* Tabs */}
                        <TaskConfigTabs
                            tabs={tabs}
                            activeTab={taskConfig.activeTab}
                            onTabChange={taskConfig.setActiveTab}
                            validationErrors={taskConfig.validationErrors}
                        />

                        {/* Tab Content */}
                        <div className="flex-1 overflow-y-auto bg-white">
                            {taskConfig.activeTab === 'capability' && (
                                <CapabilityTab
                                    config={taskConfig.config}
                                    capabilities={capabilities}
                                    selectedCapability={taskConfig.selectedCapability}
                                    onCapabilityChange={taskConfig.updateCapability}
                                    readOnly={readOnly}
                                />
                            )}
                            {taskConfig.activeTab === 'input' && (
                                <InputTab
                                    config={taskConfig.config}
                                    capability={taskConfig.selectedCapability}
                                    onInputPathChange={taskConfig.updateInputPath}
                                    onParameterChange={taskConfig.updateParameter}
                                    validationErrors={taskConfig.validationErrors}
                                    readOnly={readOnly}
                                />
                            )}
                            {taskConfig.activeTab === 'output' && (
                                <OutputTab
                                    config={taskConfig.config}
                                    onResultPlacementChange={taskConfig.updateResultPlacement}
                                    onResultPathChange={taskConfig.updateResultPath}
                                    onOutputPathChange={taskConfig.updateOutputPath}
                                    readOnly={readOnly}
                                />
                            )}
                            {taskConfig.activeTab === 'resilience' && (
                                <ResilienceTab
                                    config={taskConfig.config}
                                    capability={taskConfig.selectedCapability}
                                    onTimeoutChange={taskConfig.updateTimeout}
                                    onTimeoutPathChange={taskConfig.updateTimeoutPath}
                                    onHeartbeatChange={taskConfig.updateHeartbeat}
                                    onAddRetry={taskConfig.addRetryRule}
                                    onUpdateRetry={taskConfig.updateRetryRule}
                                    onRemoveRetry={taskConfig.removeRetryRule}
                                    onApplyPreset={taskConfig.applyRetryPreset}
                                    readOnly={readOnly}
                                />
                            )}
                            {taskConfig.activeTab === 'errors' && (
                                <ErrorsTab
                                    config={taskConfig.config}
                                    availableNodes={availableNodes}
                                    onAddCatch={taskConfig.addCatchRule}
                                    onUpdateCatch={taskConfig.updateCatchRule}
                                    onRemoveCatch={taskConfig.removeCatchRule}
                                    validationErrors={taskConfig.validationErrors}
                                    readOnly={readOnly}
                                />
                            )}
                            {taskConfig.activeTab === 'advanced' && (
                                <AdvancedTab
                                    config={taskConfig.config}
                                    aslPreview={taskConfig.toAslState()}
                                    onCommentChange={taskConfig.updateComment}
                                    validationErrors={taskConfig.validationErrors}
                                    readOnly={readOnly}
                                />
                            )}
                        </div>

                        {/* Footer */}
                        <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/50 flex justify-between items-center">
                            <div className="text-sm text-slate-500">
                                {taskConfig.isDirty && (
                                    <span className="text-amber-600">
                                        • Unsaved changes
                                    </span>
                                )}
                            </div>
                            <div className="flex gap-3">
                                <Button
                                    variant="outline"
                                    onClick={onClose}
                                >
                                    {readOnly ? 'Close' : 'Cancel'}
                                </Button>
                                {!readOnly && (
                                    <Button
                                        onClick={taskConfig.save}
                                        disabled={!taskConfig.isDirty}
                                    >
                                        Save Changes
                                    </Button>
                                )}
                            </div>
                        </div>
        </SlideDrawer>
    );
}

